import { useMemo, useState } from 'react';
import { Anchor, Badge } from '@mantine/core';
import type { ColumnDef } from '@tanstack/react-table';

import RepositoryTable from '@ayocore/platform/client/components/RepositoryTable/RepositoryTable';
import { DateCell, MoneyCell, TextCell } from '@ayocore/platform/client/components/RTable/cells';
import { useDrawer } from '@ayocore/platform/client/hooks/useDrawer';
import type { RpcRepositoryClient } from '@ayocore/platform/server/rpc/types';
import { useSupplierDocRepository } from '@ayocore/erp/accounting/client/rpc';
import type { AccountingDocument } from '@ayocore/erp/accounting/schemas/document.schema';
import { SupplierDocumentView } from '@ayocore/erp/accounting/client/components/SupplierDocs/views/SupplierDocumentView';

interface PartnerPurchasesProps {
  partnerId: number;
}

type PartnerPurchaseRow = AccountingDocument & {
  expenses_sum?: number | null;
  unlinked_sum?: number | null;
};

function buildColumns(
  onOpenDocument: (documentId: number) => void
): ColumnDef<PartnerPurchaseRow>[] {
  return [
    {
      id: 'number',
      header: 'Номер',
      accessorKey: 'number',
      size: 140,
      cell: ({ row }) => (
        <Anchor
          size='xs'
          component='button'
          type='button'
          onClick={e => {
            e.stopPropagation();
            onOpenDocument(row.original.id as number);
          }}
        >
          {row.original.number || `#${row.original.id}`}
        </Anchor>
      ),
    },
    {
      id: 'operation_date',
      header: 'Дата',
      accessorKey: 'operation_date',
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.operation_date} />,
    },
    {
      id: 'total_amount',
      header: 'Сумма, ₽',
      accessorKey: 'total_amount',
      size: 130,
      cell: ({ row }) => <MoneyCell value={row.original.total_amount} />,
    },
    {
      id: 'tax_amount',
      header: 'НДС, ₽',
      accessorKey: 'tax_amount',
      size: 120,
      cell: ({ row }) => <MoneyCell value={row.original.tax_amount} />,
    },
    {
      id: 'expenses_sum',
      header: 'Привязано, ₽',
      accessorKey: 'expenses_sum',
      size: 140,
      cell: ({ row }) => <MoneyCell value={row.original.expenses_sum} />,
    },
    {
      id: 'unlinked_sum',
      header: 'Не привязано, ₽',
      accessorKey: 'unlinked_sum',
      size: 140,
      cell: ({ row }) => <MoneyCell value={row.original.unlinked_sum} />,
    },
    {
      id: 'description',
      header: 'Комментарий',
      accessorKey: 'description',
      size: 200,
      cell: ({ row }) => <TextCell value={row.original.description} />,
    },
    {
      id: 'posted',
      header: 'Статус',
      accessorKey: 'posted',
      size: 110,
      cell: ({ row }) => (
        <Badge
          color={row.original.posted ? 'green' : 'gray'}
          variant={row.original.posted ? 'filled' : 'light'}
        >
          {row.original.posted ? 'Проведён' : 'Черновик'}
        </Badge>
      ),
    },
  ];
}

/**
 * Вкладка «Закупки» в карточке партнёра.
 * Показывает документы поставщика (SUPPLIER_DOC) по данному партнёру.
 * Клик по номеру открывает карточку документа (SupplierDocumentView).
 */
export function PartnerPurchases({ partnerId }: PartnerPurchasesProps) {
  const supplierDocRepository = useSupplierDocRepository();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = () => setRefreshTrigger(k => k + 1);

  const docDrawer = useDrawer(SupplierDocumentView);
  docDrawer.onClose(refresh);

  // supplierDocRepository — прокси, пересоздаётся каждый рендер; зависимость только от partnerId
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const repository = useMemo(
    () =>
      supplierDocRepository
        .filterByPartner(partnerId)
        .withBaseRelations()
        .withExpensesSum()
        .orderBy('operation_date', 'desc') as unknown as RpcRepositoryClient<PartnerPurchaseRow>,
    [partnerId]
  );

  const columns = useMemo(
    () =>
      buildColumns(documentId =>
        docDrawer.open({ documentId, isModal: true, onDeleted: () => docDrawer.close() })
      ),
    [docDrawer]
  );

  return (
    <RepositoryTable<PartnerPurchaseRow>
      id={`crm-partner-purchases-${partnerId}`}
      repository={repository}
      columns={columns}
      strictColumns
      showSearch={false}
      showToolbar={false}
      showViewSwitcher={false}
      defaultPageSize={10}
      refreshTrigger={refreshTrigger}
    />
  );
}
